/**
 * QMS CAPA Wizard
 * 2-step wizard for creating new CAPAs (Corrective and Preventive Actions)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight, Save, Send } from 'lucide-react';
import { useSession } from '@/hooks/useSession';
import { useCreateQmsTicket } from '../../hooks/useQmsTicket';
import { QmsStepper, type WizardStep } from './QmsStepper';
import { QmsRichTextEditor } from '../shared/QmsRichTextEditor';
import { QmsAccountSelect } from '../shared/QmsAccountSelect';
import { getQmsOrganisationId } from '../../utils/organisation';
import type {
  QmsTicket,
  QmsTicketPayload,
  QmsDepartment,
  QmsPriority,
  QmsChangeType,
} from '../../types/qms.types';

interface QmsCapaWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onCapaCreated: (capa: QmsTicket) => void;
  parentTicket?: QmsTicket; // Optional parent ticket when creating from existing ticket
}

interface FormData {
  // Basic Info
  title: string;
  department: QmsDepartment | '';
  priority: QmsPriority;
  account_id?: string;

  // Step 1: CAPA Actions
  corrective_measures: string;
  preventive_measures: string;
  due_date_corrective: string;
  due_date_preventive: string;

  // Step 2: Change Control
  change_control_required: boolean;
  change_type: QmsChangeType | '';
  change_impact_assessment: string;
}

const initialFormData: FormData = {
  title: '',
  department: '',
  priority: 3,
  account_id: undefined,
  corrective_measures: '',
  preventive_measures: '',
  due_date_corrective: '',
  due_date_preventive: '',
  change_control_required: false,
  change_type: '',
  change_impact_assessment: '',
};

export function QmsCapaWizard({
  isOpen,
  onClose,
  onCapaCreated,
  parentTicket,
}: QmsCapaWizardProps) {
  const { t } = useTranslation('qms');
  const { user } = useSession();
  const createMutation = useCreateQmsTicket();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>(() => ({
    ...initialFormData,
    title: parentTicket ? `CAPA: ${parentTicket.title}` : '',
    department: parentTicket?.department || '',
    priority: parentTicket?.priority || 3,
    account_id: parentTicket?.account_id,
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const steps: WizardStep[] = [
    { id: 'capa-actions', title: t('wizard.steps.capaActions') },
    { id: 'change-control', title: t('wizard.steps.changeControl') },
  ];

  // Validation per step
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 0) {
      if (!formData.title.trim()) {
        newErrors.title = t('validation.titleRequired');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);
      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex <= currentStep || completedSteps.includes(stepIndex)) {
      setCurrentStep(stepIndex);
    }
  };

  const buildPayload = (): QmsTicketPayload => {
    return {
      type: 'capa',
      title: formData.title,
      department: formData.department || undefined,
      priority: formData.priority,
      severity_level: 1,
      account_id: formData.account_id || undefined,
      corrective_measures: formData.corrective_measures || undefined,
      preventive_measures: formData.preventive_measures || undefined,
      due_date_corrective: formData.due_date_corrective || undefined,
      due_date_preventive: formData.due_date_preventive || undefined,
      change_control_required: formData.change_control_required,
      change_type: formData.change_type || undefined,
      change_impact_assessment: formData.change_impact_assessment || undefined,
      parent_ticket: parentTicket?._id,
      status: 'submitted',
      organisation: getQmsOrganisationId(user?.organisation),
    };
  };

  const handleSaveDraft = async () => {
    if (!validateStep(0)) return;
    setSubmitError(null);

    try {
      const payload = buildPayload();
      const capa = await createMutation.mutateAsync(payload);
      onCapaCreated(capa);
      handleClose();
    } catch (error) {
      console.error('Failed to save draft:', error);
      setSubmitError(t('errors.savingTicket'));
    }
  };

  const handleSubmit = async () => {
    for (let i = 0; i <= currentStep; i++) {
      if (!validateStep(i)) {
        setCurrentStep(i);
        return;
      }
    }
    setSubmitError(null);

    try {
      const payload = buildPayload();
      const capa = await createMutation.mutateAsync(payload);
      onCapaCreated(capa);
      handleClose();
    } catch (error) {
      console.error('Failed to create CAPA:', error);
      setSubmitError(t('errors.savingTicket'));
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setFormData({
      ...initialFormData,
      title: parentTicket ? `CAPA: ${parentTicket.title}` : '',
      department: parentTicket?.department || '',
      priority: parentTicket?.priority || 3,
      account_id: parentTicket?.account_id,
    });
    setErrors({});
    setCompletedSteps([]);
    setSubmitError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
        <div className="w-screen max-w-2xl transform transition-transform">
          <div className="flex h-full flex-col bg-white shadow-xl">
            {/* Header */}
            <div className="bg-purple-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-white">
                    {t('wizard.createCapa')}
                  </h2>
                  {parentTicket && (
                    <p className="text-sm text-purple-200 mt-1">
                      {t('wizard.linkedToTicket', { id: parentTicket._id })}
                    </p>
                  )}
                </div>
                <button
                  onClick={handleClose}
                  className="rounded-lg p-2 text-white hover:bg-purple-700 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Stepper */}
            <div className="px-6 py-4 border-b bg-gray-50">
              <QmsStepper
                steps={steps}
                currentStep={currentStep}
                completedSteps={completedSteps}
                onStepClick={handleStepClick}
              />
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Step 1: CAPA Actions */}
              {currentStep === 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('wizard.steps.capaActions')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t('wizard.capaActionsDescription')}
                  </p>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.fields.title')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        errors.title ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t('form.placeholders.title')}
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                    )}
                  </div>

                  {/* Department & Priority */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('form.fields.department')}
                      </label>
                      <select
                        value={formData.department}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            department: e.target.value as QmsDepartment,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">{t('form.placeholders.selectDepartment')}</option>
                        <option value="product">{t('departmentsFull.product')}</option>
                        <option value="sales_marketing">{t('departmentsFull.sales_marketing')}</option>
                        <option value="finance">{t('departmentsFull.finance')}</option>
                        <option value="it">{t('departmentsFull.it')}</option>
                        <option value="hr">{t('departmentsFull.hr')}</option>
                        <option value="quality">{t('departmentsFull.quality')}</option>
                        <option value="operations">{t('departmentsFull.operations')}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('form.fields.priority')}
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            priority: parseInt(e.target.value) as QmsPriority,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value={1}>{t('priority.1')}</option>
                        <option value={2}>{t('priority.2')}</option>
                        <option value={3}>{t('priority.3')}</option>
                        <option value={4}>{t('priority.4')}</option>
                        <option value={5}>{t('priority.5')}</option>
                      </select>
                    </div>
                  </div>

                  {/* Account */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.fields.account')}
                    </label>
                    <QmsAccountSelect
                      value={formData.account_id}
                      onChange={(accountId) =>
                        setFormData({ ...formData, account_id: accountId })
                      }
                    />
                  </div>

                  {/* Corrective Action */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.fields.correctiveAction')}
                    </label>
                    <QmsRichTextEditor
                      value={formData.corrective_measures}
                      onChange={(html) =>
                        setFormData({ ...formData, corrective_measures: html })
                      }
                      placeholder={t('form.placeholders.correctiveAction')}
                    />
                    <input
                      type="date"
                      value={formData.due_date_corrective}
                      onChange={(e) =>
                        setFormData({ ...formData, due_date_corrective: e.target.value })
                      }
                      className="mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Preventive Action */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.fields.preventiveAction')}
                    </label>
                    <QmsRichTextEditor
                      value={formData.preventive_measures}
                      onChange={(html) =>
                        setFormData({ ...formData, preventive_measures: html })
                      }
                      placeholder={t('form.placeholders.preventiveAction')}
                    />
                    <input
                      type="date"
                      value={formData.due_date_preventive}
                      onChange={(e) =>
                        setFormData({ ...formData, due_date_preventive: e.target.value })
                      }
                      className="mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Change Control */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('wizard.steps.changeControl')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t('wizard.changeControlDescription')}
                  </p>

                  {/* Change Control Required */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={formData.change_control_required}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            change_control_required: e.target.checked,
                          })
                        }
                        className="h-5 w-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-gray-900">
                        {t('wizard.changeControlRequired')}
                      </span>
                    </label>
                  </div>

                  {formData.change_control_required && (
                    <>
                      {/* Change Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('wizard.changeType')}
                        </label>
                        <select
                          value={formData.change_type}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              change_type: e.target.value as QmsChangeType,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">{t('wizard.selectChangeType')}</option>
                          <option value="minor">{t('wizard.changeTypes.minor')}</option>
                          <option value="major">{t('wizard.changeTypes.major')}</option>
                          <option value="critical">{t('wizard.changeTypes.critical')}</option>
                        </select>
                      </div>

                      {/* Impact Assessment */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('wizard.impactAssessment')}
                        </label>
                        <QmsRichTextEditor
                          value={formData.change_impact_assessment}
                          onChange={(html) =>
                            setFormData({ ...formData, change_impact_assessment: html })
                          }
                          placeholder={t('wizard.impactAssessmentPlaceholder')}
                        />
                      </div>
                    </>
                  )}

                  {!formData.change_control_required && (
                    <div className="p-4 bg-gray-100 rounded-lg">
                      <p className="text-sm text-gray-600">
                        {t('wizard.noChangeControlNeeded')}
                      </p>
                    </div>
                  )}

                  {/* Summary */}
                  <div className="border-t pt-4 mt-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">
                      {t('wizard.summary')}
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">{t('form.fields.title')}:</dt>
                        <dd className="text-gray-900 font-medium">{formData.title}</dd>
                      </div>
                      {formData.department && (
                        <div className="flex justify-between">
                          <dt className="text-gray-500">{t('form.fields.department')}:</dt>
                          <dd className="text-gray-900">{t(`departmentsFull.${formData.department}`)}</dd>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <dt className="text-gray-500">{t('form.fields.priority')}:</dt>
                        <dd className="text-gray-900">{t(`priority.${formData.priority}`)}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">{t('wizard.changeControlRequired')}:</dt>
                        <dd className="text-gray-900">
                          {formData.change_control_required ? t('common.yes') : t('common.no')}
                        </dd>
                      </div>
                      {parentTicket && (
                        <div className="flex justify-between">
                          <dt className="text-gray-500">{t('wizard.parentTicket')}:</dt>
                          <dd className="text-gray-900">{parentTicket.title}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t px-6 py-4 bg-gray-50">
              {/* Error display */}
              {submitError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{submitError}</p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div>
                  {currentStep > 0 && (
                    <button
                      type="button"
                      onClick={handleBack}
                      disabled={createMutation.isPending}
                      className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {t('wizard.back')}
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    disabled={createMutation.isPending}
                    className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    {t('wizard.saveDraft')}
                  </button>

                  {currentStep < steps.length - 1 ? (
                    <button
                      type="button"
                      onClick={handleNext}
                      disabled={createMutation.isPending}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                    >
                      {t('wizard.next')}
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={createMutation.isPending}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <Send className="h-4 w-4" />
                      {createMutation.isPending
                        ? t('wizard.creating')
                        : t('wizard.createAndSubmit')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
